import { useMutation, useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import { Alert, Box, Button, Chip, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { GetAccumulationAreaQuery, WasteAccumulationAreaType } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  NO_VALUE_PLACEHOLDER,
  formatContainerDate,
} from "routes/Customer/Waste/Containers/containerDisplay";
import { FacilityContainersGrid } from "routes/Customer/Waste/Containers/WasteContainersList";

import { AccumulationAreaDialog } from "./AccumulationAreaDialog";
import { DELETE_ACCUMULATION_AREA_MUTATION, GET_ACCUMULATION_AREA_QUERY } from "./api";
import { SatelliteVolumeGauge, STATUS_COLOR, STATUS_LABEL } from "./AreaCard";

type AccumulationAreaDetailData = NonNullable<GetAccumulationAreaQuery["accumulationArea"]>;

// Stable reference: OmnisearchDataGridV2 expects a memoized commandButtons
// array, and this section doesn't need any (Edit/Archive live in the header).
const NO_COMMAND_BUTTONS: ReactNode[] = [];

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function FieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

function CentralVolumeSummary({ area }: { area: AccumulationAreaDetailData }) {
  const totalOnSite = area.volume.nonAcute.total + area.volume.acute.totalVolume;
  return (
    <Stack spacing={1}>
      <Typography variant="body2">{pluralize("container", area.containerCount, true)}</Typography>
      <Typography variant="body2">{totalOnSite} gal on site</Typography>
      <Typography variant="body2" color={`${STATUS_COLOR[area.volume.status]}.main`}>
        {STATUS_LABEL[area.volume.status]}
      </Typography>
    </Stack>
  );
}

function AccumulationAreaDetailContent({ area }: { area: AccumulationAreaDetailData }) {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const isArchived = !!area.archivedAt;

  const [deleteArea, { loading: isDeleting }] = useMutation(DELETE_ACCUMULATION_AREA_MUTATION, {
    // The areas list lives on a sibling route, so it's unmounted (and its
    // refetchQueries no-op) while this mutation is in flight. Evicting the
    // area from the cache is what actually removes it before the list
    // remounts — this also covers the archive case, since archived areas
    // are filtered out server-side and would otherwise still show from the
    // stale cached list.
    update: (cache) => {
      cache.evict({ id: cache.identify({ __typename: "WasteAccumulationArea", id: area.id }) });
      cache.gc();
    },
  });

  const handleArchive = () =>
    deleteArea({
      variables: { id: area.id },
      onCompleted: (data) => {
        alerts.success(data.deleteAccumulationArea.archived ? "Area archived" : "Area deleted");
        navigate(`/o/${tenantId}/waste/accumulation-areas?facilityId=${area.facilityId}`);
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while archiving the area");
      },
    });

  return (
    <Stack spacing={3} sx={{ px: 3, py: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5">{area.name}</Typography>
          <Chip
            label={area.type === WasteAccumulationAreaType.Central ? "Central" : "Satellite"}
            size="small"
            variant="outlined"
          />
          <Chip
            label={STATUS_LABEL[area.volume.status]}
            size="small"
            color={STATUS_COLOR[area.volume.status]}
          />
          {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
        </Stack>
        {!isArchived && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Inventory2Outlined />}
              onClick={() => setArchiveDialogOpen(true)}
            >
              Archive
            </Button>
          </Stack>
        )}
      </Stack>

      {isArchived && (
        <Alert severity="info" variant="outlined">
          This area was archived on {formatContainerDate(area.archivedAt)} and is read-only.
        </Alert>
      )}

      <SectionCard title="Details">
        <Grid container spacing={2}>
          <FieldValue label="Description">{area.description ?? NO_VALUE_PLACEHOLDER}</FieldValue>
          <FieldValue label="Containers">{area.containerCount}</FieldValue>
          <FieldValue label="Latitude">{area.latitude ?? NO_VALUE_PLACEHOLDER}</FieldValue>
          <FieldValue label="Longitude">{area.longitude ?? NO_VALUE_PLACEHOLDER}</FieldValue>
        </Grid>
      </SectionCard>

      <SectionCard title="Volume">
        {area.type === WasteAccumulationAreaType.Central ? (
          <CentralVolumeSummary area={area} />
        ) : (
          <Stack spacing={1}>
            <SatelliteVolumeGauge volume={area.volume} areaName={area.name} variant="detailed" />
          </Stack>
        )}
      </SectionCard>

      <SectionCard title="Containers">
        {tenantId && (
          <Box>
            <FacilityContainersGrid
              tenantId={tenantId}
              facilityId={area.facilityId}
              pinnedSearch={`areaId:${area.id}`}
              commandButtons={NO_COMMAND_BUTTONS}
              onRowClick={(params) => navigate(`/o/${tenantId}/waste/containers/${params.row.id}`)}
              persistenceKey="area-detail-containers-table"
              showClockTooltip
            />
          </Box>
        )}
      </SectionCard>

      {editDialogOpen && (
        <AccumulationAreaDialog
          open
          areaId={area.id}
          facilityId={area.facilityId}
          onClose={() => setEditDialogOpen(false)}
        />
      )}

      <ConfirmDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        header="Archive this area?"
        info="This area will be archived and its history preserved; an area that has never held containers is deleted instead."
        confirmText="Archive"
        loading={isDeleting}
        onConfirm={handleArchive}
      />
    </Stack>
  );
}

export function AccumulationAreaDetail() {
  const { areaId } = useParams<{ areaId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_ACCUMULATION_AREA_QUERY, {
    variables: { id: areaId ?? "" },
    skip: !areaId,
  });
  const area = data?.accumulationArea;

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Accumulation areas",
      to:
        tenantId && area
          ? `/o/${tenantId}/waste/accumulation-areas?facilityId=${area.facilityId}`
          : undefined,
    },
    {
      label: area?.name ?? "Area",
    },
  ]);

  if (loading && !area) {
    return (
      <Stack spacing={2} sx={{ px: 3, py: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={48} />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Stack>
    );
  }

  if (error || !area) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Area not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The area may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <AccumulationAreaDetailContent area={area} />;
}
